import type { QueryParamType } from '@app/src/hooks/queryState'
import type { Question, QuestionAnswerClassificationRule } from '@app/src/types/resourceExplorer'
import { AnswerRuleType, QuestionAnswerRank } from '@app/src/types/resourceExplorer'
import type { FilterGroup } from '../../ResourceCollection/Filters/useFilters'
import { Operators } from '../../ResourceCollection/Filters/useFilters'
import type { EditQuestionFormData, SubmitData } from '../EditQuestion'
import type { NumberFields } from './NumberClassification'

export type QuestionAnswerClassificationRuleFormData = {
  [key in QuestionAnswerRank]: Array<QuestionAnswerClassificationRule> | undefined
}

export type QuestionNumberClassificationRuleFormData = {
  [key in NumberFields]: QuestionAnswerClassificationRule | undefined
}

export type QuestionOptionClassificationRuleFormData = {
  [key in QuestionAnswerRank]: Array<QuestionAnswerClassificationRule> | undefined
}

export const mapToAnswerClassificationDataFormData = (
  questionAnswerClassificationRules?: Question['questionAnswerClassificationRules'],
): EditQuestionFormData['questionAnswerClassificationRules'] => {
  return questionAnswerClassificationRules
    ?.filter(item => item?.ruleType === AnswerRuleType.AnswerStatus)
    ?.reduce<QuestionAnswerClassificationRuleFormData>(
      (prev, curr) => {
        if (!curr?.rank) return prev

        if (!prev[curr.rank]) {
          prev[curr.rank] = [curr]
        } else {
          prev[curr.rank]?.push(curr)
        }
        return prev
      },
      {
        Negative: undefined,
        NegativeAdverse: undefined,
        Neutral: undefined,
        Positive: undefined,
        VeryPositive: undefined,
        NotSet: undefined,
      },
    )
}

export const mapToAnswerClassificationSubmitData = (
  isWorldfavorOrganization: boolean,
  questionAnswerClassificationRules?: EditQuestionFormData['questionAnswerClassificationRules'],
): SubmitData['questionAnswerClassificationRules'] => {
  if (!isWorldfavorOrganization) return

  return Object.values(questionAnswerClassificationRules ?? {})
    .filter(Boolean)
    .flatMap(value => value ?? [])
}

export const mapToNumberClassificationDataFormData = (
  questionAnswerClassificationRules?: Question['questionAnswerClassificationRules'],
): EditQuestionFormData['questionNumberClassificationRules'] => {
  return questionAnswerClassificationRules
    ?.filter(
      item =>
        item?.ruleType === AnswerRuleType.NumberEqualTo ||
        item?.ruleType === AnswerRuleType.NumberGreaterThan ||
        item?.ruleType === AnswerRuleType.NumberLowerThan,
    )
    ?.reduce<QuestionNumberClassificationRuleFormData>(
      (prev, curr) => {
        if (!curr?.ruleType) return prev

        if (
          curr.ruleType === AnswerRuleType.NumberEqualTo ||
          curr.ruleType === AnswerRuleType.NumberGreaterThan ||
          curr.ruleType === AnswerRuleType.NumberLowerThan
        ) {
          prev[curr.ruleType] = curr
        }
        return prev
      },
      {
        [AnswerRuleType.NumberEqualTo]: undefined,
        [AnswerRuleType.NumberGreaterThan]: undefined,
        [AnswerRuleType.NumberLowerThan]: undefined,
      },
    )
}

export const mapToNumberClassificationSubmitData = (
  isWorldfavorOrganization: boolean,
  questionNumberClassificationRules: EditQuestionFormData['questionNumberClassificationRules'],
): SubmitData['questionAnswerClassificationRules'] => {
  if (!isWorldfavorOrganization) return

  return Object.entries(questionNumberClassificationRules ?? {})
    .filter(([_, value]) => Boolean(value?.value))
    .map(([key, value]) => ({
      rank: value?.rank ?? QuestionAnswerRank.NotSet,
      ruleType: AnswerRuleType[key as keyof typeof AnswerRuleType],
      value: value?.value,
    }))
}

export const mapToOptionClassificationDataFormData = (
  questionAnswerClassificationRules?: Question['questionAnswerClassificationRules'],
): EditQuestionFormData['questionOptionClassificationRules'] => {
  return questionAnswerClassificationRules
    ?.filter(item => item?.ruleType === AnswerRuleType.SelectedValue)
    ?.reduce<QuestionOptionClassificationRuleFormData>(
      (prev, curr) => {
        if (!curr?.rank) return prev

        if (!prev[curr.rank]) {
          prev[curr.rank] = [curr]
        } else {
          prev[curr.rank]?.push(curr)
        }

        return prev
      },
      {
        Negative: undefined,
        NegativeAdverse: undefined,
        Neutral: undefined,
        Positive: undefined,
        VeryPositive: undefined,
        NotSet: undefined,
      },
    )
}

export const mapToOptionClassificationSubmitData = (
  isWorldfavorOrganization: boolean,
  questionAnswerClassificationRules?: EditQuestionFormData['questionOptionClassificationRules'],
): SubmitData['questionAnswerClassificationRules'] => {
  if (!isWorldfavorOrganization) return

  return Object.values(questionAnswerClassificationRules ?? {})
    .filter(Boolean)
    .flatMap(value => value ?? [])
}

export const mapToClassificationSubmitData = (isWorldfavorOrganization: boolean, formData: EditQuestionFormData) => {
  const answerClassification = mapToAnswerClassificationSubmitData(
    isWorldfavorOrganization,
    formData.questionAnswerClassificationRules,
  )
  const numberClassification = mapToNumberClassificationSubmitData(
    isWorldfavorOrganization,
    formData.questionNumberClassificationRules,
  )
  const optionClassification = mapToOptionClassificationSubmitData(
    isWorldfavorOrganization,
    formData.questionOptionClassificationRules,
  )

  return [answerClassification, numberClassification, optionClassification]
    .flatMap(x => x ?? [])
    .filter(item => item?.rank !== QuestionAnswerRank.NotSet)
}

export const normalizeFilterGroups = (filterGroups: FilterGroup[]): FilterGroup[] => {
  return filterGroups.map(group => ({
    ...group,
    filters: group.filters.map(f => {
      const operator = (f?.operator as string).toLowerCase()
      let value = f.value

      if ((operator === Operators.EqualTo || operator === Operators.NotEqualTo) && Array.isArray(value)) {
        value = value[0]
      }

      return {
        ...f,
        value,
      }
    }),
  }))
}

export const mapToQueryParamType = (filter: FilterGroup): QueryParamType | undefined => {
  const firstFilter = filter.filters?.[0]
  if (!firstFilter || firstFilter.value === undefined || firstFilter.value === null) return

  const operator = firstFilter.operator

  let filterValue: string | string[]
  if (operator === Operators.EqualTo || operator === Operators.NotEqualTo) {
    filterValue = firstFilter.value.toString()
  } else {
    filterValue = Array.isArray(firstFilter.value)
      ? firstFilter.value.map(v => v.toString())
      : [firstFilter.value.toString()]
  }

  return {
    name: filter.name,
    value: filterValue,
    operator,
  }
}
